<template>
    <article class="phu-card" :class="{ 'phu-card--fixed': isFixed }" :data-phu-id="phu.id">
        <header class="phu-card__header">
            <h3 class="phu-card__title">{{ phu.name }}</h3>
        </header>

        <PhuGrid :slots="visibleSlots" :columns="columnCount" :rows="rowCount" :phu-id="phu.id" :locked="isFixed">
            <template #default="{ item }">
                <PhuSlot
                    :slot-item="item"
                    :product="products[item.product_id]"
                    :now="now"
                    :draggable="isDraggable(item)"
                    :holding="isHolding(item)"
                    :armed="isArmed(item)"
                    :dragging="isDragging(item)"
                    :drop-zone="isDropZone(item)"
                    :drop-target="isHover(item)"
                    :show-time="showTime"
                    @press="onSlotPress(item, $event)"
                />
            </template>
        </PhuGrid>

        <div v-if="dragSlot" ref="ghost" class="phu-card__ghost" :style="ghostStyle">
            <PhuSlot :slot-item="dragSlot" :product="products[dragSlot.product_id]" :now="now" :show-time="showTime" draggable />
        </div>
    </article>
</template>

<script>
import { cloneDeep } from 'lodash'
import PhuGrid from './PhuGrid.vue'
import PhuSlot from './PhuSlot.vue'
import { resolveSlotState } from '../utils/slotState'
import { SLOT_STATES } from '@/store/static/phuProduction'
const RUNTIME_FIELDS = ['units', 'batch_id', 'started_at', 'ready_at', 'expires_at']
const DRAG_THRESHOLD = 8
const HOLD_CANCEL = 24
const HOLD_MS = 1000
const MOVE_MS = 200

export default {
    name: 'PhuCard',
    components: { PhuGrid, PhuSlot },
    props: {
        phu: { type: Object, required: true },
        products: { type: Object, required: true },
        now: { type: Number, required: true },
        showTime: { type: Boolean, default: false },
        locked: { type: Boolean, default: false },
        externalProductId: { type: [String, Number], default: null },
        hoverSlotId: { type: [String, Number], default: null },
        phus: { type: Array, default: () => [] }
    },
    watch: {
        dirty(editing) {
            this.$store.commit('phuProduction/setPhuEditing', { phuId: this.phu.id, editing })
        },
        'phu.slots'() {
            if (this.drag) {
                return
            }
            this.draftSlots = []
            this.dirty = false
        }
    },
    data() {
        return {
            dirty: false,
            draftSlots: [],
            press: null,
            armed: false,
            holdTimer: null,
            drag: null,
            dropTargetId: null,
            dropExternal: false,
            moveTimer: null
        }
    },
    computed: {
        isFixed() {
            return this.locked || Boolean(this.phu.fixed_layout)
        },
        columnCount() {
            return Number(this.phu.columns) || 1
        },
        rowCount() {
            return Number(this.phu.rows) || 0
        },
        visibleSlots() {
            return this.draftSlots.length ? this.draftSlots : this.phu.slots
        },
        dragSlot() {
            if (!this.drag) {
                return null
            }
            return this.findSlot(this.drag.slotId)
        },
        ghostStyle() {
            if (!this.drag) {
                return null
            }
            return {
                left: `${this.drag.x}px`,
                top: `${this.drag.y}px`,
                width: `${this.drag.width}px`,
                height: `${this.drag.height}px`
            }
        }
    },
    methods: {
        isDraggable(slot) {
            return this.canDrag(slot)
        },
        isHolding(slot) {
            return Boolean(this.press) && !this.armed && !this.drag && this.press.slotId === slot.id
        },
        isArmed(slot) {
            return this.armed && Boolean(this.press) && !this.drag && this.press.slotId === slot.id
        },
        isDragging(slot) {
            return Boolean(this.drag) && this.drag.slotId === slot.id
        },
        canDrag(slot) {
            return !this.isFixed && Boolean(slot.product_id) && !this.isIdle(slot)
        },
        isDropZone(slot) {
            if (this.isFixed || !slot.product_id) {
                return false
            }
            const productId = this.drag ? this.drag.productId : this.externalProductId
            if (!productId || (this.drag && slot.id === this.drag.slotId)) {
                return false
            }
            return String(slot.product_id) === String(productId)
        },
        isHover(slot) {
            return String(this.dropTargetId) === String(slot.id) || String(this.hoverSlotId) === String(slot.id)
        },
        findSlot(slotId) {
            return this.visibleSlots.find((slot) => slot.id === slotId)
        },
        onSlotPress(slot, event) {
            if (this.drag || event.button > 0) {
                return
            }
            if (!this.canDrag(slot)) {
                this.$emit('slot-click', this.slotClickPayload(slot))
                return
            }
            this.press = {
                slotId: slot.id,
                productId: slot.product_id,
                x: event.clientX,
                y: event.clientY,
                pointerId: event.pointerId,
                moved: false
            }
            this.armed = false
            this.holdTimer = setTimeout(this.armDrag, HOLD_MS)
            window.addEventListener('pointermove', this.onPointerMove)
            window.addEventListener('pointerup', this.onPointerUp)
            window.addEventListener('pointercancel', this.onPointerUp)
        },
        armDrag() {
            this.holdTimer = null
            if (!this.press || this.press.moved) {
                return
            }
            this.armed = true
        },
        onPointerMove(event) {
            if (!this.press || event.pointerId !== this.press.pointerId) {
                return
            }
            const dx = event.clientX - this.press.x
            const dy = event.clientY - this.press.y
            const distance = dx * dx + dy * dy
            if (!this.armed) {
                if (distance >= HOLD_CANCEL * HOLD_CANCEL) {
                    this.press.moved = true
                    clearTimeout(this.holdTimer)
                    this.holdTimer = null
                }
                return
            }
            if (!this.drag) {
                if (distance < DRAG_THRESHOLD * DRAG_THRESHOLD) {
                    return
                }
                this.beginDrag(event)
                return
            }
            this.drag.x = event.clientX - this.drag.offsetX
            this.drag.y = event.clientY - this.drag.offsetY
            this.markDropTarget(event.clientX, event.clientY)
        },
        beginDrag(event) {
            const slot = this.findSlot(this.press.slotId)
            if (slot && resolveSlotState(slot, this.now) === SLOT_STATES.COOKING) {
                this.$snackbar.error({
                    message: this.$t('phu_production.errors.cooking_move'),
                    closeable: true
                })
                this.unbindPointer()
                this.press = null
                this.armed = false
                return
            }
            const cell = this.cellElement(this.press.slotId)
            if (!cell) {
                return
            }
            const rect = cell.getBoundingClientRect()
            this.drag = {
                slotId: this.press.slotId,
                productId: this.press.productId,
                pointerId: this.press.pointerId,
                width: rect.width,
                height: rect.height,
                offsetX: this.press.x - rect.left,
                offsetY: this.press.y - rect.top,
                x: rect.left,
                y: rect.top
            }
            this.drag.x = event.clientX - this.drag.offsetX
            this.drag.y = event.clientY - this.drag.offsetY
            this.markDropTarget(event.clientX, event.clientY)
            this.$emit('drag-product', this.drag.productId)
        },
        markDropTarget(x, y) {
            const target = this.targetAt(x, y)
            this.dropTargetId = target ? target.id : null
            this.dropExternal = target ? target.external : false
            this.$emit('drag-hover', this.dropTargetId)
        },
        onPointerUp(event) {
            if (!this.press || event.pointerId !== this.press.pointerId) {
                return
            }
            if (this.drag) {
                this.markDropTarget(event.clientX, event.clientY)
            }
            const press = this.press
            const drag = this.drag
            const targetId = this.dropTargetId
            const external = this.dropExternal
            const wasArmed = this.armed
            this.unbindPointer()
            this.press = null
            this.armed = false
            this.dropTargetId = null
            this.dropExternal = false
            if (!drag) {
                if (wasArmed || press.moved) {
                    return
                }
                const slot = this.findSlot(press.slotId)
                if (slot) {
                    this.$emit('slot-click', this.slotClickPayload(slot))
                }
                return
            }
            const sourceEl = this.cellElement(drag.slotId)
            if (!sourceEl) {
                this.releaseDrag()
                this.drag = null
                return
            }
            const targetEl = targetId ? document.querySelector(`[data-slot-id="${targetId}"]`) : null
            const destination = targetEl ? targetEl.getBoundingClientRect() : sourceEl.getBoundingClientRect()
            if (targetEl && sourceEl && !external) {
                this.flyCell(targetEl, sourceEl.getBoundingClientRect())
            }
            const sourceSlot = this.findSlot(drag.slotId)
            this.animateGhost(destination, () => {
                this.clearCellMotion()
                this.releaseDrag()
                this.drag = null
                if (targetId && external && sourceSlot) {
                    this.draftSlots = []
                    this.dirty = false
                    this.$emit('move-across', { source: this.slotClickPayload(sourceSlot), targetSlotId: targetId })
                    return
                }
                if (targetId) {
                    this.commitSwap(drag.slotId, targetId)
                }
            })
        },
        releaseDrag() {
            this.$emit('drag-product', null)
            this.$emit('drag-hover', null)
        },
        targetAt(x, y) {
            if (!this.drag) {
                return null
            }
            const cell = this.cellAt(x, y)
            if (cell) {
                const located = this.locateSlot(cell.getAttribute('data-slot-id'))
                if (!located || String(located.slot.id) === String(this.drag.slotId)) {
                    return null
                }
                if (!this.canReceive(located.phu, located.slot)) {
                    return null
                }
                return {
                    id: located.slot.id,
                    external: String(located.phu.id) !== String(this.phu.id)
                }
            }
            const card = this.cardAt(x, y)
            if (!card || card === this.$el) {
                return null
            }
            const phu = this.phus.find((item) => String(item.id) === card.getAttribute('data-phu-id'))
            const slot = phu ? this.emptySlotForProduct(phu) : null
            if (!phu || !slot) {
                return null
            }
            return { id: slot.id, external: true }
        },
        cellAt(x, y) {
            return this.elementAt(x, y, '[data-slot-id]')
        },
        cardAt(x, y) {
            return this.elementAt(x, y, '[data-phu-id]')
        },
        elementAt(x, y, selector) {
            const elements = document.elementsFromPoint ? document.elementsFromPoint(x, y) : [document.elementFromPoint(x, y)]
            const ghost = this.$refs.ghost
            for (let index = 0; index < elements.length; index += 1) {
                const element = elements[index]
                if (!element || !element.closest || (ghost && ghost.contains(element))) {
                    continue
                }
                const match = element.closest(selector)
                if (match) {
                    return match
                }
            }
            return null
        },
        locateSlot(slotId) {
            const groups = this.phus.length ? this.phus : [this.phu]
            for (let index = 0; index < groups.length; index += 1) {
                const phu = groups[index]
                const slot = (phu.slots || []).find((item) => String(item.id) === String(slotId))
                if (slot) {
                    return { phu, slot }
                }
            }
            return null
        },
        canReceive(phu, slot) {
            if (this.phuIsLocked(phu) || !slot.product_id) {
                return false
            }
            return String(slot.product_id) === String(this.drag.productId)
        },
        emptySlotForProduct(phu) {
            if (this.phuIsLocked(phu)) {
                return null
            }
            return (phu.slots || []).find((slot) => this.canReceive(phu, slot) && !slot.batch_id && !Number(slot.units)) || null
        },
        phuIsLocked(phu) {
            return String(phu.id) === String(this.phu.id) ? this.isFixed : this.isFixed || Boolean(phu.fixed_layout)
        },
        cellElement(slotId) {
            return this.$el.querySelector(`[data-slot-id="${slotId}"]`)
        },
        flyCell(element, toRect) {
            const from = element.getBoundingClientRect()
            element.style.zIndex = '2'
            element.style.transition = `transform ${MOVE_MS}ms ease`
            element.style.transform = `translate(${toRect.left - from.left}px, ${toRect.top - from.top}px)`
            this.movingCell = element
        },
        animateGhost(toRect, onDone) {
            const ghost = this.$refs.ghost
            if (!ghost || !toRect) {
                onDone()
                return
            }
            const from = ghost.getBoundingClientRect()
            const dx = toRect.left - from.left
            const dy = toRect.top - from.top
            if (Math.abs(dx) < 1 && Math.abs(dy) < 1) {
                onDone()
                return
            }
            let finished = false
            const finish = () => {
                if (finished) {
                    return
                }
                finished = true
                clearTimeout(this.moveTimer)
                ghost.removeEventListener('transitionend', onEnd)
                onDone()
            }
            const onEnd = (transitionEvent) => {
                if (transitionEvent.target !== ghost || transitionEvent.propertyName !== 'transform') {
                    return
                }
                finish()
            }
            ghost.addEventListener('transitionend', onEnd)
            ghost.style.transition = `transform ${MOVE_MS}ms ease`
            ghost.style.transform = `translate(${dx}px, ${dy}px)`
            this.moveTimer = setTimeout(finish, MOVE_MS + 40)
        },
        commitSwap(sourceId, targetId) {
            if (!this.draftSlots.length) {
                this.draftSlots = cloneDeep(this.phu.slots)
            }
            this.swapSlots(sourceId, targetId)
            this.savePositions()
        },
        clearCellMotion() {
            const element = this.movingCell
            this.movingCell = null
            if (!element) {
                return
            }
            this.$nextTick(() => {
                element.style.transition = 'none'
                element.style.transform = ''
                element.style.zIndex = ''
            })
        },
        swapSlots(sourceId, targetId) {
            const source = this.draftSlots.find((slot) => slot.id === sourceId)
            const target = this.draftSlots.find((slot) => slot.id === targetId)
            if (!source || !target) {
                return
            }
            if (this.isIdle(source) && this.isIdle(target)) {
                const position = source.position
                source.position = target.position
                target.position = position
                return
            }
            RUNTIME_FIELDS.forEach((field) => {
                const value = source[field]
                source[field] = target[field]
                target[field] = value
            })
        },
        isIdle(slot) {
            return !slot.batch_id && !Number(slot.units)
        },
        slotClickPayload(slot) {
            const snapshot = {
                units: slot.units,
                batch_id: slot.batch_id,
                started_at: slot.started_at,
                ready_at: slot.ready_at,
                expires_at: slot.expires_at
            }
            return {
                id: slot.id,
                product_id: slot.product_id,
                position: slot.position,
                ...snapshot,
                release: () => this.clearDraftSlot(slot.id),
                reduce: (units) => this.reduceDraftSlot(slot.id, units),
                restore: () => this.restoreDraftSlot(slot.id, snapshot)
            }
        },
        restoreDraftSlot(slotId, snapshot) {
            const draft = this.draftSlots.find((item) => item.id === slotId)
            if (!draft) {
                return
            }
            draft.units = snapshot.units
            draft.batch_id = snapshot.batch_id
            draft.started_at = snapshot.started_at
            draft.ready_at = snapshot.ready_at
            draft.expires_at = snapshot.expires_at
        },
        reduceDraftSlot(slotId, units) {
            const draft = this.draftSlots.find((item) => item.id === slotId)
            if (!draft) {
                return
            }
            draft.units = Math.max(0, Number(draft.units) - units)
        },
        clearDraftSlot(slotId) {
            const draft = this.draftSlots.find((item) => item.id === slotId)
            if (!draft) {
                return
            }
            draft.units = 0
            draft.batch_id = null
            draft.started_at = null
            draft.ready_at = null
            draft.expires_at = null
        },
        savePositions() {
            this.$emit('save-positions', { phuId: this.phu.id, slots: this.draftSlots })
            this.dirty = false
            this.draftSlots = []
        },
        unbindPointer() {
            clearTimeout(this.holdTimer)
            this.holdTimer = null
            window.removeEventListener('pointermove', this.onPointerMove)
            window.removeEventListener('pointerup', this.onPointerUp)
            window.removeEventListener('pointercancel', this.onPointerUp)
        }
    },
    beforeDestroy() {
        this.unbindPointer()
        clearTimeout(this.moveTimer)
        if (this.dirty) {
            this.$store.commit('phuProduction/setPhuEditing', { phuId: this.phu.id, editing: false })
        }
    }
}
</script>

<style lang="scss" scoped>
@import '~@/components/domain/phu-production/styles/tokens';

.phu-card {
    @include display-flex();
    @include flex-direction(column);
    gap: 4px;
    width: 100%;
    min-width: 0;
    padding: 8px 8px 8px;
    border: 1px solid $neutro-dark;
    border-radius: $phu-radius-s;
    background-color: $phu-surface;
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.04), 0 4px 8px rgba(0, 0, 0, 0.06);

    &__header {
        @include display-flex();
        @include align-items(center);
        @include justify-content(space-between);
        min-height: 30px;
        padding: 4px 8px;
        border-radius: $phu-radius-s;
        background-color: $neutro-t80;
    }

    &__title {
        margin: 0;
        font-family: $text-bold;
        font-size: 1rem;
        line-height: 18px;
        color: $phu-text;
    }

    &__ghost {
        @include display-flex();
        position: fixed;
        z-index: 30;
        pointer-events: none;
        filter: drop-shadow(0 8px 16px rgba(0, 0, 0, 0.18));
    }
}
</style>
